import {useCallback} from 'react';
import axios from '../utils/axiosConfig';
import {errorHandler} from '../utils/errorHandler';
import toast from 'react-hot-toast';
import {LLMSettings, LlmSettingsResponse} from "../pages/Settings/types";
import {useDispatch} from "react-redux";
import {setLLMSettingsLoading, setLLMSettings, updateLLMModel} from "../redux/slices/settings";

const useLlmService = () => {
  const dispatch = useDispatch();

  const showError = (error: any) => {
    errorHandler.showError(error);
  };

  const getLlmSettings = useCallback(async (): Promise<LLMSettings | null> => {
    dispatch(setLLMSettingsLoading(true));
    try {
      const response = await axios.get<LlmSettingsResponse>('/integration/llm');
      const raw: any = (response?.data as any)?.settings;
      const isEmptyObject = raw && typeof raw === 'object' && Object.keys(raw).length === 0;
      const hasAnyField = raw && (raw.provider || raw.api_key || raw.model);
      const next = !raw || isEmptyObject || !hasAnyField ? null : (raw as LLMSettings);
      dispatch(setLLMSettings(next));
      return next || null;
    } catch (error) {
      const status = (error as any)?.response?.status;
      if (status !== 404) {
        showError(error);
      }
      dispatch(setLLMSettings(null));
      return null;
    } finally {
      dispatch(setLLMSettingsLoading(false));
    }
  }, []);

  const createLlmSettings = useCallback(async (payload: LLMSettings) => {
    dispatch(setLLMSettingsLoading(true));
    try {
      const response = await axios.post<{ message?: string }>('/integration/llm', payload);
      if (response?.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success('LLM settings saved.');
      }
      dispatch(setLLMSettings(payload));
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      dispatch(setLLMSettingsLoading(false));
    }
  }, []);

  const updateLlmSettings = useCallback(async (payload: LLMSettings) => {
    dispatch(setLLMSettingsLoading(true));
    try {
      const response = await axios.put<{ message?: string }>('/integration/llm', payload);
      if (response?.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success('LLM settings updated.');
      }
      dispatch(setLLMSettings(payload));
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      dispatch(setLLMSettingsLoading(false));
    }
  }, []);

  const updateLlmModel = useCallback(async (model: string) => {
    dispatch(setLLMSettingsLoading(true));
    try {
      await axios.put('/integration/llm/model', {
        model: model
      });

      dispatch(updateLLMModel(model));
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      dispatch(setLLMSettingsLoading(false));
    }
  }, []);

  return {
    getLlmSettings,
    createLlmSettings,
    updateLlmModel,
    updateLlmSettings,
  };
};

export default useLlmService;